import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { storeRequestApi } from "../../../api/storeRequestApi";
import { onboardingApi } from "../../../api/onboardingApi";
import { useNotifications } from "../../../context/NotificationContext";
import {
  CreditCard,
  Building2,
  CheckCircle2,
  Package,
  Layers,
  Globe,
  Headphones,
  ArrowRight,
  AlertCircle,
  Sparkles,
  ShieldCheck
} from "lucide-react";
import "./SubscriptionDetails.css";

const SubscriptionDetails = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [onboardingSteps, setOnboardingSteps] = useState([]);
  const { socket } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const subscription =
    subscriptions.find(
      (item) => item.StoreRequestID === selectedRequestId,
    ) || subscriptions[0] || null;

  const fetchSubscriptionDetails = useCallback(async () => {
    try {
      setError("");
      const response = await storeRequestApi.getClientRequests();
      const requests = response.data || [];
      if (requests.length > 0) {
        setSubscriptions(requests);
        const selected =
          requests.find((item) => item.StoreRequestID === selectedRequestId) ||
          requests[0];
        setSelectedRequestId(selected.StoreRequestID);
        const stepsResponse = await onboardingApi.getStoreSteps(
          selected.StoreRequestID,
        );
        setOnboardingSteps(stepsResponse.data || []);
      } else {
        setSubscriptions([]);
        setSelectedRequestId(null);
        setOnboardingSteps([]);
      }
    } catch (err) {
      setError("Failed to load subscription details. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedRequestId]);

  useEffect(() => {
    fetchSubscriptionDetails();
  }, [fetchSubscriptionDetails]);

  useEffect(() => {
    if (!socket) return undefined;
    const refreshRequest = ({ storeRequestId } = {}) => {
      if (
        !storeRequestId ||
        subscriptions.some((item) => item.StoreRequestID === storeRequestId)
      ) {
        fetchSubscriptionDetails();
      }
    };
    socket.on("request:updated", refreshRequest);
    socket.on("platform:updated", fetchSubscriptionDetails);
    return () => {
      socket.off("request:updated", refreshRequest);
      socket.off("platform:updated", fetchSubscriptionDetails);
    };
  }, [fetchSubscriptionDetails, socket, subscriptions]);

  const getSubmittedFields = (rawData) => {
    let data = rawData;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch {
        return [];
      }
    }
    if (!data || typeof data !== "object" || Array.isArray(data)) return [];
    const fields = [];
    const visit = (value, prefix = "") => {
      Object.entries(value).forEach(([key, fieldValue]) => {
        if (
          /(password|secret|token|private.?key|credential|contractdocument|image|logo|banner)/i.test(
            key,
          )
        ) {
          return;
        }
        const label = prefix ? `${prefix} / ${key}` : key;
        if (fieldValue === null || fieldValue === undefined) return;
        if (Array.isArray(fieldValue)) {
          const values = [];
          fieldValue.forEach((item, index) => {
            if (item && typeof item === "object" && !Array.isArray(item)) {
              visit(item, `${label} ${index + 1}`);
            } else if (
              item !== null &&
              ["string", "number", "boolean"].includes(typeof item)
            ) {
              values.push(String(item));
            }
          });
          if (values.length) fields.push([label, values.join(", ")]);
          return;
        }
        if (typeof fieldValue === "object") {
          visit(fieldValue, label);
          return;
        }
        const displayValue = String(fieldValue).trim();
        if (displayValue) fields.push([label, displayValue]);
      });
    };
    visit(data);
    return fields.slice(0, 8);
  };

  const getRequestUpdates = () => {
    let requirements = subscription?.AdditionalRequirements;
    if (typeof requirements === "string") {
      try {
        requirements = JSON.parse(requirements);
      } catch {
        requirements = {};
      }
    }
    if (!requirements || typeof requirements !== "object") return [];
    const labels = {
      businessInfo: "Business information",
      domain: "Domain preferences",
      design: "Store design",
      products: "Products",
      agreement: "Agreement",
      contractAcceptance: "Contract name and consent",
      invoice: "Invoice details",
      package: "Selected package",
    };
    return Object.entries(requirements)
      .filter(
        ([key, value]) =>
          key !== "contractDocument" &&
          value &&
          typeof value === "object" &&
          getSubmittedFields(value).length > 0,
      )
      .map(([key, value]) => ({
        key,
        title: labels[key] || key.replace(/([A-Z])/g, " $1"),
        fields: getSubmittedFields(value),
      }));
  };

  const getRequestSnapshot = () => {
    let requirements = subscription?.AdditionalRequirements;
    if (typeof requirements === "string") {
      try {
        requirements = JSON.parse(requirements);
      } catch {
        return {};
      }
    }
    return requirements && typeof requirements === "object"
      ? requirements
      : {};
  };

  const parseFeatures = (features) => {
    if (!features) return {};
    if (typeof features === "object") return features;
    try {
      const parsed = JSON.parse(features);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  };

  const features = parseFeatures(
    subscription?.PackageFeatures ?? subscription?.Features,
  );
  const requestSnapshot = getRequestSnapshot();
  const savedPackage = requestSnapshot.package || {};
  const savedInvoice = requestSnapshot.invoice || {};
  const currentPackagePrice = Number(
    subscription?.PackagePrice ?? subscription?.Price ?? 0,
  );
  const packagePrice =
    currentPackagePrice ||
    Number(savedInvoice.packageAmount ?? savedPackage.price ?? 0) ||
    Number(subscription?.LatestPaymentAmount || 0);
  const setupFee =
    Number(subscription?.PackageSetupFee ?? subscription?.SetupFee ?? 0) ||
    Number(savedInvoice.packageSetupFee ?? savedPackage.setupFee ?? 0);
  const billingPeriod =
    subscription?.PackageBillingPeriod ??
    subscription?.BillingPeriod ??
    "month";

  if (loading) {
    return (
      <div className="subscription-loading">
        <div className="loading-card">
          <div className="spinner-ring">
            <div></div><div></div><div></div><div></div>
          </div>
          <p className="loading-title">Loading subscription...</p>
          <p className="loading-subtitle">Fetching plan and billing details</p>
        </div>

      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-details-page">
        <div className="error-card">
          <AlertCircle className="error-icon" size={32} />
          <h3>Unable to load subscription</h3>
          <p>{error}</p>
          <button className="btn-secondary" onClick={fetchSubscriptionDetails}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="subscription-details-page">
        <div className="empty-state-card">
          <div className="empty-icon-wrap">
            <CreditCard size={32} />
          </div>
          <h3>No Active Subscription</h3>
          <p>You don't have an active store subscription yet.</p>
          <button
            className="btn-primary"
            onClick={() => navigate("/request-store")}
          >
            <span>Request a Store</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-details-page">
      <header className="page-header">
        <div className="header-badge">
          <ShieldCheck size={14} />
          <span>Billing & Plan</span>
        </div>
        <h2>Subscription Details</h2>
        <p className="header-description">
          View each store request, its plan, billing, and saved setup details.
        </p>
      </header>

      {subscriptions.length > 1 && (
        <div className="subscription-picker">
          <label htmlFor="subscription-request">Store subscription</label>
          <select
            id="subscription-request"
            value={subscription.StoreRequestID}
            onChange={(event) => setSelectedRequestId(event.target.value)}
          >
            {subscriptions.map((item) => (
              <option key={item.StoreRequestID} value={item.StoreRequestID}>
                {item.BusinessName || item.PackageName || "Store"} ·{" "}
                {item.RequestNumber || item.StoreRequestID} ·{" "}
                {String(item.Status || "pending").replace(/_/g, " ")}
              </option>
            ))}
          </select>
          <span>{subscriptions.length} store requests</span>
        </div>
      )}

      <div className="dashboard-grid">
        {/* ---------- CURRENT PLAN CARD ---------- */}
        <div className="dashboard-card plan-card">
          <div className="card-header-row">
            <span className="card-subtitle-label">Active Plan</span>
            <span className={`status-pill status-${subscription.Status}`}>
              <span className="dot"></span>
              {subscription.Status?.replace(/_/g, " ")}
            </span>
          </div>

          <div className="plan-pricing-wrap">
            <h3 className="plan-title">{subscription.PackageName}</h3>
            <div className="price-display">
              <span className="price-amount">R{packagePrice.toFixed(2)}</span>
              <span className="price-cycle">/ {billingPeriod}</span>
            </div>
            <div className="subscription-amount-details">
              <div><span>Plan price</span><strong>R{packagePrice.toFixed(2)}</strong></div>
              {setupFee > 0 && (
                <div><span>Setup fee</span><strong>R{setupFee.toFixed(2)}</strong></div>
              )}
              {subscription.LatestPaymentAmount !== null &&
                subscription.LatestPaymentAmount !== undefined && (
                  <div>
                    <span>Latest payment</span>
                    <strong>
                      {subscription.LatestPaymentCurrency || "ZAR"}{" "}
                      {Number(subscription.LatestPaymentAmount).toFixed(2)}
                    </strong>
                  </div>
                )}
              {subscription.PaymentStatus && (
                <div>
                  <span>Payment status</span>
                  <strong>{String(subscription.PaymentStatus).replace(/_/g, " ")}</strong>
                </div>
              )}
            </div>
          </div>

          <div className="plan-card-footer">
            <div className="guarantee-badge">
              <Sparkles size={14} />
              <span>Includes full feature access & ongoing support</span>
            </div>
          </div>
        </div>

        {/* ---------- STORE INFORMATION CARD ---------- */}
        <div className="dashboard-card store-info-card">
          <div className="card-title-wrap">
            <Building2 size={20} className="card-icon" />
            <h3>Store Information</h3>
          </div>

          <div className="info-list">
            <div className="info-item">
              <span className="info-label">Business Name</span>
              <span className="info-value">{subscription.BusinessName || "N/A"}</span>
            </div>

            <div className="info-item">
              <span className="info-label">Request ID</span>
              <span className="info-value code-font">#{subscription.RequestNumber}</span>
            </div>

            <div className="info-item">
              <span className="info-label">Submitted On</span>
              <span className="info-value">
                {new Date(subscription.SubmittedDate).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>

            {subscription.ApprovedDate && (
              <div className="info-item">
                <span className="info-label">Approved On</span>
                <span className="info-value">
                  {new Date(subscription.ApprovedDate).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
            )}

            {subscription.LaunchedDate && (
              <div className="info-item">
                <span className="info-label">Launched On</span>
                <span className="info-value highlight-value">
                  {new Date(subscription.LaunchedDate).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ---------- PLAN FEATURES CARD ---------- */}
        <div className="dashboard-card features-card span-full">
          <div className="card-title-wrap">
            <Layers size={20} className="card-icon" />
            <div>
              <h3>Plan Specifications & Limits</h3>
              <p className="card-subtext">Features included in your current subscription level</p>
            </div>
          </div>

          <div className="features-grid">
            <div className="feature-box">
              <div className="feature-icon-wrap">
                <Package size={20} />
              </div>
              <div className="feature-details">
                <span className="feature-title">Product Limit</span>
                <span className="feature-value">
                  {subscription.PackageProductLimit ??
                    subscription.ProductLimit ??
                    "Unlimited"}
                </span>
              </div>
            </div>

            <div className="feature-box">
              <div className="feature-icon-wrap">
                <Headphones size={20} />
              </div>
              <div className="feature-details">
                <span className="feature-title">Support Level</span>
                <span className="feature-value">
                  {features.support || "Standard Support"}
                </span>
              </div>
            </div>

            <div className="feature-box">
              <div className="feature-icon-wrap">
                <Globe size={20} />
              </div>
              <div className="feature-details">
                <span className="feature-title">Domain Type</span>
                <span className="feature-value">
                  {features.domain || "Subdomain"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <section className="dashboard-card submitted-updates-card span-full">
            <div className="card-title-wrap">
              <CheckCircle2 size={20} className="card-icon" />
              <div>
                <h3>Your onboarding updates</h3>
                <p className="card-subtext">
                  Information saved as you completed your store setup.
                </p>
              </div>
            </div>
            {getRequestUpdates().length ||
            onboardingSteps.some((step) => getSubmittedFields(step.Data).length) ? (
              <div className="submitted-updates-list">
                {getRequestUpdates().map((section) => (
                  <article className="submitted-update" key={section.key}>
                    <div className="submitted-update-heading">
                      <strong>{section.title}</strong>
                      <span>Submitted with this request</span>
                    </div>
                    <dl>
                      {section.fields.map(([key, value]) => (
                        <div key={key}>
                          <dt>
                            {key
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (letter) => letter.toUpperCase())}
                          </dt>
                          <dd>{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </article>
                ))}
                {onboardingSteps
                  .filter((step) => getSubmittedFields(step.Data).length)
                  .map((step) => (
                    <article
                      className="submitted-update"
                      key={step.OnboardingStepID}
                    >
                      <div className="submitted-update-heading">
                        <strong>
                          {step.Title || step.StepName || step.Name || step.StepKey}
                        </strong>
                        <span>
                          {step.CompletedDate
                            ? new Date(step.CompletedDate).toLocaleDateString()
                            : String(step.ProgressStatus || "saved").replace(/_/g, " ")}
                        </span>
                      </div>
                      <dl>
                        {getSubmittedFields(step.Data).map(([key, value]) => (
                          <div key={key}>
                            <dt>
                              {key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (letter) => letter.toUpperCase())}
                            </dt>
                            <dd>{String(value)}</dd>
                          </div>
                        ))}
                      </dl>
                    </article>
                  ))}
              </div>
            ) : (
              <p className="submitted-updates-empty">
                Your saved onboarding details will appear here as you complete each step.
              </p>
            )}
        </section>
      </div>
    </div>
  );
};

export default SubscriptionDetails;